/**
 * Two-factor authentication on the Settings screen.
 *
 * `twoFactorEnabled` was a dead schema field — no UI, no enrolment, and nothing
 * checking it at sign-in (QA ledger AUTH-010). This is the screen that turns it
 * on, and it is shaped around the two ways people lose access to their own
 * account: a QR that never scanned, and a phone that is gone.
 *
 * So enrolment is two steps with the secret readable as text alongside the QR,
 * and the backup codes are presented as a step of their own rather than a line
 * of small print under a success message.
 */

import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Label,
  Badge,
  Alert,
  AlertTitle,
  AlertDescription,
} from '@lognai/synapse-design-system-v2';
import { QrCode } from './QrCode';
import { BackupCodes } from './BackupCodes';
import {
  useTwoFactorStatus,
  useStartTwoFactorSetup,
  useEnableTwoFactor,
  useDisableTwoFactor,
  useRegenerateBackupCodes,
  twoFactorErrorMessage,
} from '../../hooks/useTwoFactor';
import type { TwoFactorSetup } from '../../services/api/auth';

/** Base32 in groups of four, which is the only way it can be typed accurately. */
const grouped = (secret: string): string => secret.replace(/(.{4})/g, '$1 ').trim();

type Panel =
  | { kind: 'idle' }
  | { kind: 'enrolling'; setup: TwoFactorSetup }
  | { kind: 'codes'; codes: string[]; reissued: boolean }
  | { kind: 'disabling' }
  | { kind: 'reissuing' };

export const TwoFactorCard: React.FC = () => {
  const status = useTwoFactorStatus();
  const startSetup = useStartTwoFactorSetup();
  const enable = useEnableTwoFactor();
  const disable = useDisableTwoFactor();
  const reissue = useRegenerateBackupCodes();

  const [panel, setPanel] = React.useState<Panel>({ kind: 'idle' });
  const [code, setCode] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const reset = () => {
    setPanel({ kind: 'idle' });
    setCode('');
    setPassword('');
    setError(null);
  };

  const handleStart = async () => {
    setError(null);
    try {
      setPanel({ kind: 'enrolling', setup: await startSetup.mutateAsync() });
    } catch (err) {
      setError(twoFactorErrorMessage(err, 'Could not start setup'));
    }
  };

  const handleEnable = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const { backupCodes } = await enable.mutateAsync(code.trim());
      setCode('');
      setPanel({ kind: 'codes', codes: backupCodes, reissued: false });
    } catch (err) {
      setError(twoFactorErrorMessage(err, 'That code did not match'));
    }
  };

  const handleDisable = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await disable.mutateAsync(password);
      reset();
    } catch (err) {
      setError(twoFactorErrorMessage(err, 'Could not turn two-factor off'));
    }
  };

  const handleReissue = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const { backupCodes } = await reissue.mutateAsync(password);
      setPassword('');
      setPanel({ kind: 'codes', codes: backupCodes, reissued: true });
    } catch (err) {
      setError(twoFactorErrorMessage(err, 'Could not replace your backup codes'));
    }
  };

  const enabled = status.data?.enabled ?? false;
  const remaining = status.data?.backupCodesRemaining ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>Two-factor authentication</CardTitle>
            <CardDescription>
              Ask for a code from your authenticator app as well as your password.
            </CardDescription>
          </div>
          {status.isSuccess && (
            <Badge tone={enabled ? 'success' : 'neutral'}>{enabled ? 'On' : 'Off'}</Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {status.isLoading && <p className="text-sm text-muted-foreground">Checking…</p>}
        {status.isError && (
          <p className="text-sm text-destructive">
            Couldn’t check whether two-factor is on. Reload to try again.
          </p>
        )}

        {error && (
          <div role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* --- The codes, shown once, whichever step produced them --- */}
        {panel.kind === 'codes' && (
          <BackupCodes
            codes={panel.codes}
            title={panel.reissued ? 'Your new backup codes' : 'Two-factor is on. Save these codes.'}
            description={
              panel.reissued
                ? 'Your previous codes stopped working the moment these were made. Each of these can be used once.'
                : 'Each code signs you in once if you lose your device. They are shown only now — we store them hashed and cannot show them again.'
            }
            onDone={reset}
          />
        )}

        {/* --- Enrolment step two: prove the app holds the secret --- */}
        {panel.kind === 'enrolling' && (
          <form onSubmit={handleEnable} className="space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              {/* `bg-white` is deliberate and stays: a QR code needs a white
                  quiet zone to scan reliably, whatever the app's theme. */}
              <div className="rounded-lg border border-border bg-white p-3">
                <QrCode
                  value={panel.setup.otpauthUri}
                  label="Scan this with your authenticator app"
                />
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <p className="text-sm text-foreground">
                  Scan this with Google Authenticator, 1Password, Authy or any TOTP app.
                </p>
                {/* The key in text is not a fallback nobody needs: a desktop
                    browser showing a QR to a phone is the easy case, and
                    everything else — a phone browser, a screen reader, a remote
                    session — needs something typeable. */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    Can’t scan? Enter this key by hand
                  </Label>
                  <p className="break-all rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-sm tracking-wider">
                    {grouped(panel.setup.secret)}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="enable-code">Enter the 6-digit code your app shows</Label>
              <Input
                id="enable-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="max-w-[12rem]"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                maxLength={7}
                autoFocus
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={enable.isPending || code.trim().length === 0}>
                {enable.isPending ? 'Verifying…' : 'Turn on two-factor'}
              </Button>
              <Button type="button" variant="ghost" onClick={reset}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* --- Password re-auth, for the two destructive actions --- */}
        {(panel.kind === 'disabling' || panel.kind === 'reissuing') && (
          <form
            onSubmit={panel.kind === 'disabling' ? handleDisable : handleReissue}
            className="space-y-4"
          >
            {panel.kind === 'disabling' && (
              <Alert tone="warning">
                <AlertTitle>This removes the second factor from your account</AlertTitle>
                <AlertDescription>
                  Your backup codes will stop working too. You can set it up again at any time.
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="twofactor-password">Confirm your password</Label>
              <Input
                id="twofactor-password"
                type="password"
                autoComplete="current-password"
                className="max-w-xs"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                variant={panel.kind === 'disabling' ? 'destructive' : 'default'}
                disabled={
                  password.length === 0 ||
                  (panel.kind === 'disabling' ? disable.isPending : reissue.isPending)
                }
              >
                {panel.kind === 'disabling'
                  ? disable.isPending
                    ? 'Turning off…'
                    : 'Turn off two-factor'
                  : reissue.isPending
                    ? 'Generating…'
                    : 'Generate new codes'}
              </Button>
              <Button type="button" variant="ghost" onClick={reset}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* --- Resting state --- */}
        {panel.kind === 'idle' && status.isSuccess && !enabled && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Two-factor is off. Your password is all that protects this account.
            </p>
            <Button type="button" onClick={handleStart} disabled={startSetup.isPending}>
              {startSetup.isPending ? 'Preparing…' : 'Set up two-factor'}
            </Button>
          </div>
        )}

        {panel.kind === 'idle' && status.isSuccess && enabled && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              On since{' '}
              {status.data?.enabledAt
                ? new Date(status.data.enabledAt).toLocaleDateString()
                : 'recently'}
              .
            </p>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-foreground">
                {remaining} backup {remaining === 1 ? 'code' : 'codes'} left
              </span>
              {/* Running out is only a problem at the worst possible moment, so
                  it is said before then rather than after. */}
              {remaining <= 3 && (
                <Badge tone={remaining === 0 ? 'destructive' : 'warning'}>
                  {remaining === 0 ? 'None left' : 'Running low'}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setError(null);
                  setPanel({ kind: 'reissuing' });
                }}
              >
                Generate new backup codes
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setError(null);
                  setPanel({ kind: 'disabling' });
                }}
              >
                Turn off
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
